/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import { Segment, Button, Icon } from 'semantic-ui-react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  conversationActive,
  getMessagesBackwardByTime,
  selectConsultPending,
  getOngoingConsult,
  conversationRetrieve,
} from 'Redux/modules/webChat/actions';
import { consultAccepted, resetConsultStatus } from 'Redux/modules/webChat/consult/actions';
import { addModal } from 'Redux/modules/modal/actions';
import { shape, number } from 'prop-types';
import { CONSULT_ACCEPTED, CONSULT_PENDING } from 'Constants/consults';
import { findClinicIdFromClinicsMapping, isMNDRLite } from 'Helpers/consult-utils';
import { CONVERSATION_MODEL_RESPONSE, MESSAGES_COUNT_PER_PAGE } from 'Constants/webchat';
import { generateIncludeParams } from 'Helpers/utils';
import moment from 'moment';
import useAuth from 'Hooks/useAuth';
import { savePickUpConsult } from 'Helpers/pickUpConsult';
import useActiveClinicTab from 'Containers/Teleconsult/hooks/useActiveClinicTab';

import RequestContent from '../RequestContent/RequestContent';

const MNDRLiteNote =
  'Note: The MaNaDr Lite consult is only supporting Video Call feature for now. The patient cannot receive messages from you.';

const CONVERSATION_INCLUDES_PARAM = {
  include: generateIncludeParams(CONVERSATION_MODEL_RESPONSE),
};

const EXISTING_ACTIVE_CONSULT_ERROR = 'EXISTING_ACTIVE_CONSULT_ERROR';

function formatSecondsToSS(seconds) {
  try {
    if (!seconds) return '';
    const remainingSeconds = seconds % 60;
    return String(remainingSeconds).padStart(2, '0');
  } catch (e) {
    return '';
  }
}

const MessagePendingRequest = props => {
  const { message, consult, doctorId } = props;

  const {
    id: consultId,
    status,
    extraData,
    patient: { id: patientId },
    forPatient: { id: forPatientId },
    service,
    isAwaitingPatientConfirmation,
  } = consult;

  const { sessionId } = useAuth();

  const dispatch = useDispatch();
  const isPendingConsult = status === CONSULT_PENDING;
  const consultState = useSelector(state => state.webChat.consults, shallowEqual);
  const { setActiveClinicTab } = useActiveClinicTab();
  const [seconds, setSeconds] = useState(0);

  // Note: timer ref to handle 2 cases
  // - Start automatically when the consult is picked by this doctor
  // - Start when this doctor click accept a consult
  const intervalRef = useRef(null);

  const { status: consultStatus, error } = consultState;

  const loading =
    consultStatus.accepting === 0 ||
    consultStatus.acceptPending === 0 ||
    consultStatus.ongoingConsult === 0;
  const isMNDRLiteConsult = isMNDRLite(extraData?.source?.type);

  const displayError =
    (consultStatus.accepting === 2 || consultStatus.acceptPending === 2) &&
    !!error &&
    error?.data?.errorCode !== EXISTING_ACTIVE_CONSULT_ERROR;

  // Check if this doctor is picking this consult
  const doctorIsPicking =
    isAwaitingPatientConfirmation && extraData?.pickUpData?.pickUpByDoctorId === doctorId;

  const startCooldownTimer = confirmedAt => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const now = moment();
    const confirmTime = moment.unix(confirmedAt).isValid() ? moment.unix(confirmedAt) : now;
    const duration = now.isAfter(confirmTime) ? 0 : confirmTime.diff(now, 'second');
    setSeconds(duration);

    intervalRef.current = setInterval(() => {
      setSeconds(prevSeconds => {
        if (prevSeconds <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }

        return prevSeconds - 1;
      });
    }, 1000);
  };

  const acceptConsult = async () => {
    try {
      // Note: this step is doctor just picking up the consult request
      const payload = { status: CONSULT_ACCEPTED };
      const acceptRes = await dispatch(consultAccepted(doctorId, consultId, payload, sessionId));
      startCooldownTimer(acceptRes?.data?.extraData?.pickUpData?.confirmedAt);
      savePickUpConsult(consultId, doctorId);
    } catch (e) {
      const errorCode = e?.data?.errorCode;
      if (errorCode === EXISTING_ACTIVE_CONSULT_ERROR) {
        const currentTime = new Date().valueOf();
        const ongoingConsult = await dispatch(
          getOngoingConsult(+doctorId, +patientId, {
            forPatientId: +forPatientId,
            serviceId: service?.id,
          }),
        );

        if (ongoingConsult?.data?.conversation) {
          const conversationId = ongoingConsult?.data?.conversation?.id;

          const conversationClinicId = findClinicIdFromClinicsMapping(
            ongoingConsult?.data?.clinics || [],
            doctorId,
          );

          dispatch(
            addModal({
              type: 'alreadyHaveConsult',
              id: 1,
              data: {
                doctorId: 1,
                onConfirmAction: async () => {
                  setActiveClinicTab(conversationClinicId, true);
                  await dispatch(selectConsultPending(-1));
                  dispatch(conversationRetrieve(conversationId, CONVERSATION_INCLUDES_PARAM));
                  dispatch(conversationActive(conversationId));
                  await dispatch(conversationActive(conversationId, false, currentTime));
                  if (conversationId) {
                    dispatch(
                      getMessagesBackwardByTime({
                        conversationId,
                        count: MESSAGES_COUNT_PER_PAGE,
                        timestamp: currentTime,
                      }),
                    );
                  }
                },
              },
            }),
          );
        }
      }
    }
  };

  const renderBtnTitle = () => {
    if (doctorIsPicking) {
      return (
        <span>
          <Icon name="spinner" loading /> Connecting to patient... {formatSecondsToSS(seconds)}
        </span>
      );
    }

    return 'Start Consult';
  };

  useEffect(() => () => dispatch(resetConsultStatus()), [dispatch]);

  // auto start timer if this consult is picked by this doctor
  useEffect(() => {
    if (doctorIsPicking) {
      startCooldownTimer(extraData?.pickUpData?.confirmedAt);
    }

    return () => clearInterval(intervalRef.current);
  }, [doctorIsPicking, extraData?.pickUpData?.confirmedAt]);

  return (
    <div className="message-request-new">
      <Segment className="content">
        <div className="title">
          <div className="label">
            <strong>CONSULT REQUEST</strong>
          </div>
        </div>
        <RequestContent message={message} />
        <div className="actions">
          {isPendingConsult && (
            <Button
              icon={!doctorIsPicking ? 'check' : null}
              content={renderBtnTitle()}
              primary
              fluid
              onClick={acceptConsult}
              loading={loading}
              disabled={loading || doctorIsPicking}
            />
          )}
          {displayError && <p className="error">{error?.data?.message}</p>}
        </div>
      </Segment>
      {isMNDRLiteConsult && (
        <div className="note">
          <p>{MNDRLiteNote}</p>
        </div>
      )}
    </div>
  );
};

MessagePendingRequest.propTypes = {
  message: shape({}).isRequired,
  consult: shape({}).isRequired,
  doctorId: number.isRequired,
};

export default MessagePendingRequest;
